import { channel } from 'redux-saga';
import { put, call, fork, select, take, cancel } from 'redux-saga/effects';
import * as Selectors from '../reducers/selectors';
import {
  addProjectImage,
  addProjectImageRef,
  autoFillProject,
  sagaAutoFillProject,
} from '../ducks/project';
import {
  toggleAutoFillPromptModal,
  toggleAutoFillWaitModal,
  toggleImageManagement,
} from '../ducks/navigation';
import PicabooConstants from '../../app/common/PicabooConstants';

import {
  addImageToGallery,
  appService,
  getEmptyWellsCount,
} from './zoomServices';

import {
  UpdateImageUploadPercent,
  FinishedUploading,
  FAILED_PRE_RESIZE,
  FAILED_UPLOADS,
} from '../ducks/addPhotos/imagesToUpload';

import EXIF from './exif';
import logEvent from '../utils/TagManager';

import {
  SHOW_IMAGE_UPLOAD_FAILED,
  SHOW_PRE_RESIZE_FAILED,
} from '../ducks/modals';
import { isMobile } from '../components/PicabooResponsive';

import {
  loadGalleries,
  loadGallery,
  setSelectedGallery,
} from '../ducks/addPhotos/addPhotos';
import { isPrints } from '../reducers/selectors';

import { autoFillPages } from '../ducks/changesets';
import { dotNetApiBase } from '../config';
import { emitImageUploadFailedEvent } from './ct-events';

function uploadFileForm(uploadFile, imageID, name, source) {
  let data = new FormData();
  data.append('file', uploadFile, name);
  data.append('source', source || PicabooConstants.IMAGE_SOURCE.CUSTOMER);
  return data;
}

function changeRequestForm(projectPK, file) {
  let data = new FormData();

  data.append('projectID', projectPK);
  data.append('installationID', '281FA621-B7CC-BFFC-28D0-7ED236F13EE0');
  var blob = new Blob([file]);
  data.append('changeRequestXML', blob, 'changeRequestXML');

  return data;
}

function getExifData(uploadFile) {
  return new Promise(function (resolve, reject) {
    EXIF.getData(uploadFile, function () {
      resolve(this.exifdata);
    });
  });
}

function imageReady(im, path) {
  return new Promise(function (resolve, reject) {
    im.crossOrigin = 'Anonymous';
    im.onload = resolve;
    im.onError = reject;
    im.src = path;
  });
}

function* sendChangeSets(changeRequestValues, section) {
  yield put(addProjectImage(changeRequestValues));
  yield put(
    addProjectImageRef({ id: changeRequestValues.imageID, sectionID: section }),
  );
}

function* addToGallery(images, galleryName) {
  try {
    yield addImageToGallery(images, galleryName);
    yield put(loadGallery(yield select(Selectors.selectedGalleryPK)));
    //only reset selected gallery if we are uploading to a specific project in editor
    const project = yield select(Selectors.projectMetadataSelector);
    if (project) yield put(setSelectedGallery(null));
    yield put(loadGalleries());
  } catch (e) {
    console.error('failed to add to gallery', e);
  }
}

export function* checkAndAutoFillProject(project, imagesUploaded) {
  if (project.get('type') === PicabooConstants.PRODUCT_TYPES.PRINTS) {
    let emptyWellObject = yield getEmptyWellsCount(project.get('projectPK'));

    if (emptyWellObject.emptyCount === emptyWellObject.totalCount) {
      if (imagesUploaded < emptyWellObject.totalCount) {
        yield put(autoFillProject());
      } else {
        yield put(toggleAutoFillPromptModal(true));
      }
    } else {
      if (imagesUploaded < emptyWellObject.emptyCount) {
        yield put(autoFillPages('', true, false));
      } else {
        yield put(toggleAutoFillPromptModal(true));
      }
    }

    let isWaitModalOpen = yield select(
      (s) => s.navigation.showAutoFillWaitModal,
    );
    if (isWaitModalOpen) {
      yield put(toggleAutoFillWaitModal());
    }
  }
}

function* uploadImages(action) {
  let project = null;
  let section = null;
  if (yield select((state) => state.project)) {
    project = yield select(Selectors.projectMetadataSelector);
    section = yield select(Selectors.sectionIdSelector);
  }

  let onUploadProgress = function* (progressEvent) {
    let percentCompleted = progressEvent.loaded / progressEvent.total;
    yield put(UpdateImageUploadPercent(uploadFile, percentCompleted * 100));
  };

  const imagesToAddToGallery = [];

  const uploadChannel = yield call(channel);

  const uploadChannels = [];

  // create 3 worker 'threads'
  const NUMBER_OF_CONCURRENT_UPLOAD = 3;
  for (let i = 0; i < NUMBER_OF_CONCURRENT_UPLOAD; i++) {
    uploadChannels.push(yield fork(handleUploadRequest, uploadChannel));
  }

  for (let i = 0; i < action.images.length; i++) {
    yield put(uploadChannel, action.images[i]);
  }

  function* handleUploadRequest(chan) {
    while (true) {
      const uploadFile = yield take(chan);
      // process the request
      {
        // const uploadFile =

        let success = false;
        let changeRequestValues = {};
        let skipped = false; // pre-resize skip

        let imageAudit = '';
        let imageUploadError = '';
        let uploadImageID;
        try {
          let imageType = 'n';
          if (!uploadFile.imageID && uploadFile.linkURL === undefined) {
            let im = new Image();
            let imageID = uploadFile.id
              ? uploadFile.id.toUpperCase()
              : uploadFile.imageID
                ? uploadFile.imageID.toUpperCase()
                : '';
            uploadImageID = imageID;
            let blob = null;
            if (uploadFile.isLocalFile) {
              im.src = URL.createObjectURL(uploadFile.file);
            } else {
              yield imageReady(im, uploadFile.highResPath);
              let exifResult = yield call(getExifData, im);
              blob = new Blob([exifResult.binFile], { type: 'image/png' });
            }

            const MAX_FILE_SIZE = 1024 * 1024 * 100; //100MB

            let tooLarge = false;
            if (
              uploadFile &&
              uploadFile.file &&
              uploadFile.file.size > MAX_FILE_SIZE
            ) {
              tooLarge = true;
            } else if (blob && blob.size > MAX_FILE_SIZE) {
              tooLarge = true;
            }

            if (tooLarge) {
              const msg = {
                type: FAILED_PRE_RESIZE,
                image: { imageID: imageID, thumbPath: uploadFile.thumbPath },
              };

              yield put(msg);
              yield put(FinishedUploading({ imageID: imageID }, false));
              skipped = true; // pre-resize skip
            } else {
              let config = yield call(createSource);
              yield fork(watchMessages, config, uploadFile);

              let data = uploadFileForm(
                blob || uploadFile.file,
                imageID,
                uploadFile.name,
                uploadFile.source,
              );

              const url = 'api/UserImageUpload/upload';
              let response = yield call(appService().post, url, data, config);

              const imageData = response?.data;
              if (imageData?.clientID) {
                logEvent('Add Photos', 'Photo uploaded successfully', '', 0);
                success = true;
                imageID = imageData.clientID;

                // /* Bug 17894 - photos rotated in Microsoft Windows Photo Viewer (and presumably others) change the
                //  * date taken to the time they were rotated and put in a "DateTimeOriginal" so use that for the
                //  * date taken if it is available
                //  */
                // let dateTaken = exifResult.DateTimeOriginal || exifResult.DateTime || (uploadFile.file ? new Date(uploadFile.file.lastModified): (uploadFile.dateCreated ? uploadFile.dateCreated : Date.now()));

                changeRequestValues.imageID = imageID;
                changeRequestValues.dateTaken = imageData.dateTaken;
                changeRequestValues.filename = imageData.fileName; // uploadFile.name ? uploadFile.name : (uploadFile.file ? uploadFile.file.name : (uploadFile.filename ? uploadFile.filename : '' ));
                changeRequestValues.filehash = imageData.fileHash;
                changeRequestValues.width = imageData.width; // /<width>(.*)<\/width>/.exec(response.data)[1];
                changeRequestValues.height = imageData.height; // /<height>(.*)<\/height>/.exec(response.data)[1];
                changeRequestValues.orientation = 1;
                changeRequestValues.integrationData =
                  uploadFile.integrationData;

                changeRequestValues.serverImage = '0';

                changeRequestValues.missingImage = '0';
                changeRequestValues.dateLastModified = uploadFile.file
                  ? new Date(uploadFile.file.lastModified)
                  : Date.now();
                changeRequestValues.showInMediaTray = 'true';

                imageAudit =
                  imageAudit +
                  '\n     changeRequestValues.imageID = ' +
                  changeRequestValues.imageID +
                  '\n' +
                  '     changeRequestValues.dateTaken = ' +
                  changeRequestValues.dateTaken +
                  '\n' +
                  '     changeRequestValues.filename = ' +
                  changeRequestValues.filename +
                  '\n' +
                  '     changeRequestValues.width = ' +
                  changeRequestValues.width +
                  '\n' +
                  '     changeRequestValues.height = ' +
                  changeRequestValues.height +
                  '\n' +
                  '     changeRequestValues.orientation = ' +
                  changeRequestValues.orientation +
                  '\n' +
                  '     changeRequestValues.imageID = ' +
                  changeRequestValues.imageID +
                  '\n' +
                  '     changeRequestValues.integrationData = ' +
                  changeRequestValues.integrationData +
                  '\n' +
                  '     changeRequestValues.dateLastModified = ' +
                  changeRequestValues.dateLastModified +
                  '\n';
              } else {
                logEvent('Add Photos', 'Photo upload failed', '', 0);
                //console.error('upload failed', response);

                imageAudit =
                  imageAudit +
                  '\nUpload failed:' +
                  '\n' +
                  '    ' +
                  response.data;
                imageUploadError = response.data;
              }
            }
          } else {
            //This is a image from an previous project or QR code.
            success = true;
            changeRequestValues = Object.assign({}, uploadFile);
            uploadFile.id = uploadFile.id
              ? uploadFile.id.toUpperCase()
              : uploadFile.imageID
                ? uploadFile.imageID.toUpperCase()
                : '';
            uploadImageID = uploadFile.id;
            imageType = uploadFile.imageType == 'n' ? 'n' : 'q';
            if (imageType != 'q') {
              imageAudit =
                imageAudit +
                '\nExisting image found (previously uploaded path) - ' +
                uploadFile.id +
                '\n' +
                '    ' +
                uploadFile.id;
            }
          }
          if (!skipped && success) {
            changeRequestValues.imageType = imageType;
            changeRequestValues.showInMediaTray = 'true';
            changeRequestValues.serverImage = '0';
            if (uploadFile.linkURL) {
              changeRequestValues.imageID = uploadFile.id;
            }

            if (changeRequestValues.imageType === 'q') {
              changeRequestValues.highResPath = null;
              changeRequestValues.thumbPath = null;
            }

            if (section) {
              yield fork(sendChangeSets, changeRequestValues, section);
            }
            yield put(FinishedUploading({ imageID: uploadImageID }, true));
            if (action.galleryName) {
              imagesToAddToGallery.push(changeRequestValues.imageID);
            }
          } else if (!skipped) {
            yield put(FinishedUploading({ imageID: uploadImageID }, false));
            yield call(
              failedUpload,
              uploadImageID,
              uploadFile.thumbPath,
              imageAudit,
              JSON.stringify(imageUploadError),
            );
          }
        } catch (error) {
          console.error('Failed', error);
          success = false;
          if (error?.response?.status === 401) {
            for (let i = 0; i < uploadChannels.length; i++) {
              yield cancel(uploadChannels[i]);
            }
          } else {
            //Remove the image from the upload queue
            yield put(FinishedUploading({ imageID: uploadImageID }, false));
            //look for image audit if it got that far (successfully called upload endpoint and got a 200 response)
            //otherwise just log out the image they tried to upload
            const imageDetails =
              imageAudit.length > 0 ? imageAudit : JSON.stringify(uploadFile);
            yield call(
              failedUpload,
              uploadImageID,
              uploadFile.thumbPath,
              imageDetails,
              JSON.stringify(error),
            );
          }
        }
      }

      const imagesToUpload = yield select(Selectors.imagesToUploadCount);
      const failedImagesUpload = yield select(Selectors.imagesFailedUpload);
      const failedImagesPreResize = yield select(
        Selectors.imagesFailedPreResize,
      );

      if (imagesToUpload == 0) {
        if (project) {
          yield* sagaAutoFillProject();
          const isPrints = yield select(Selectors.isPrints);
          if (isMobile() && isPrints) {
            yield put(toggleImageManagement());
          }
        }

        if (action.galleryName) {
          yield fork(addToGallery, imagesToAddToGallery, action.galleryName);
        }

        if (failedImagesUpload.length > 0) {
          yield put({
            type: SHOW_IMAGE_UPLOAD_FAILED,
            showmodal: true,
          });
        }

        if (failedImagesPreResize.length > 0) {
          yield put({
            type: SHOW_PRE_RESIZE_FAILED,
            showmodal: true,
          });
        }
      }
    }
  }

  function* failedUpload(imageID, thumbPath, imageDetails, error) {
    const msg = {
      type: FAILED_UPLOADS,
      image: { imageID, thumbPath },
    };

    yield put(msg);

    emitImageUploadFailedEvent(imageID, thumbPath, imageDetails, error);
  }

  function readFileAsDataURL(inputFile) {
    const temporaryFileReader = new FileReader();

    return new Promise((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        reject(new DOMException('Problem parsing input file.'));
      };

      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };
      temporaryFileReader.readAsDataURL(inputFile);
    });
  }

  function loadFileAsImage(dataURL) {
    return new Promise((resolve, reject) => {
      const image = document.createElement('img');
      image.onload = (event) => {
        resolve(event.target);
      };
      image.onerror = (event) => {
        reject();
      };
      image.src = dataURL;
    });
  }

  function canvasToBlob(canvas, type) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        'image/jpeg',
        0.92,
      );
    });
  }

  function* watchMessages(msgSource, uploadFile) {
    let txs = yield call(msgSource.nextMessage);
    while (txs) {
      yield put(UpdateImageUploadPercent(uploadFile, txs * 100));
      txs = yield call(msgSource.nextMessage);
    }
  }

  function createSource() {
    let deferred;

    return {
      onUploadProgress: (event) => {
        let percentCompleted = event.loaded / event.total;

        if (deferred) {
          deferred.resolve(percentCompleted);
          deferred = null;
        }
      },
      nextMessage() {
        if (!deferred) {
          deferred = {};
          deferred.promise = new Promise(
            (resolve) => (deferred.resolve = resolve),
          );
        }
        return deferred.promise;
      },
    };
  }
}

export default uploadImages;
